<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Dialog' });

interface EgsDialogProps {
	modelValue?: boolean;
	visible?: boolean | null;
	title?: string;
	header?: string;
	widthClass?: string;
	modal?: boolean;
	closable?: boolean;
	closeOnEscape?: boolean;
	closeOnOverlay?: boolean;
	dismissableMask?: boolean | null;
	draggable?: boolean;
	position?: 'top' | 'center' | 'bottom' | string;
	maximizable?: boolean;
	contentClass?: string;
	contentStyle?: string | Record<string, string>;
	breakpoints?: Record<string, string>;
}

const props = withDefaults(defineProps<EgsDialogProps>(), {
	modelValue: false,
	visible: null,
	title: '',
	header: '',
	widthClass: '',
	modal: true,
	closable: true,
	closeOnEscape: true,
	closeOnOverlay: true,
	dismissableMask: null,
	draggable: false,
	position: 'center',
	maximizable: false,
	contentClass: ''
});
const emit = defineEmits<{
	'update:modelValue': [value: boolean];
	'update:visible': [value: boolean];
	close: [];
	hide: [];
	show: [];
}>();
const attrs = useAttrs();
const panelRef = ref<HTMLElement | null>(null);
const maximized = ref(false);
const show = computed(() => props.visible ?? props.modelValue);
const resolvedTitle = computed(() => props.header || props.title);
const positionClass = computed(() => (props.position === 'top' ? 'items-start' : props.position === 'bottom' ? 'items-end' : 'items-center'));
const canDismissMask = computed(() => props.dismissableMask ?? props.closeOnOverlay);

function close(): void {
	if (!show.value) return;
	emit('update:modelValue', false);
	emit('update:visible', false);
	emit('close');
}
function onOverlayClick(event: MouseEvent): void {
	if (canDismissMask.value && event.target === event.currentTarget) close();
}
function onKeydown(event: KeyboardEvent): void {
	if (show.value && props.closeOnEscape && event.key === 'Escape') close();
}

watch(show, (open, wasOpen) => {
	if (open && !wasOpen) {
		emit('show');
		requestAnimationFrame(() => panelRef.value?.focus());
	}
	if (!open && wasOpen) emit('hide');
});
onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
	<Teleport to="body">
		<Transition enter-active-class="transition duration-150 ease-out" enter-from-class="opacity-0" leave-active-class="transition duration-100 ease-in" leave-to-class="opacity-0">
			<div v-if="show" :class="['fixed inset-0 z-[10000] flex justify-center p-4', positionClass, modal ? 'bg-slate-950/55 backdrop-blur-[1px]' : 'pointer-events-none bg-transparent']" role="presentation" @mousedown="onOverlayClick">
				<section
					ref="panelRef"
					tabindex="-1"
					v-bind="{ ...attrs, ...(modal ? { 'aria-modal': 'true' } : {}) }"
					role="dialog"
					:class="[
						'pointer-events-auto flex max-h-[calc(100vh-2rem)] w-[min(32rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-surface bg-[var(--egs-content-background)] text-color shadow-modal outline-none',
						widthClass,
						maximized ? '!h-full !max-h-none !w-full !max-w-none' : '',
						attrs['class']
					]"
					@mousedown.stop
				>
					<header class="flex shrink-0 items-center justify-between gap-4 border-b border-surface px-5 py-4">
						<slot name="header"
							><h2 class="truncate text-base font-semibold text-color">{{ resolvedTitle }}</h2></slot
						>
						<div class="ml-auto flex items-center gap-1">
							<button
								v-if="maximizable"
								type="button"
								class="inline-flex h-8 w-8 items-center justify-center rounded-full text-color-secondary hover:bg-[var(--egs-content-hover)]"
								:aria-label="maximized ? 'Restore dialog' : 'Maximize dialog'"
								@click="maximized = !maximized"
							>
								<font-awesome-icon :icon="maximized ? 'compress' : 'expand'" />
							</button>
							<button v-if="closable" type="button" class="inline-flex h-8 w-8 items-center justify-center rounded-full text-color-secondary hover:bg-[var(--egs-content-hover)]" aria-label="Close dialog" @click="close">
								<font-awesome-icon icon="xmark" />
							</button>
						</div>
					</header>
					<div :class="['min-h-0 flex-1 overflow-y-auto px-5 py-4 text-color', contentClass]" :style="contentStyle"><slot /></div>
					<footer v-if="$slots['footer']" class="flex shrink-0 items-center justify-end gap-3 border-t border-surface px-5 py-4"><slot name="footer" /></footer>
				</section>
			</div>
		</Transition>
	</Teleport>
</template>
